import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CITIES, formatPriceUnit } from "../vegetable_catalog";

function formatRupee(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return "--";
  }
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(value)}`;
}

function withUnit(priceText, unit) {
  if (!priceText || priceText === "--") {
    return "--";
  }
  return `${priceText}${formatPriceUnit(unit)}`;
}

function formatWholesale(item) {
  if (!item) {
    return "--";
  }
  return withUnit(formatRupee(item.wholesale), item.unit);
}

function formatRetail(item) {
  if (!item) {
    return "--";
  }
  let text = "--";
  if (item.retailMin != null && item.retailMax != null) {
    text =
      item.retailMin === item.retailMax
        ? formatRupee(item.retailMin)
        : `₹${item.retailMin}–${item.retailMax}`;
  } else if (item.retailText && item.retailText !== "--") {
    text = `₹${item.retailText}`;
  }
  return withUnit(text, item.unit);
}

function formatCell(item, priceType) {
  return priceType === "retail" ? formatRetail(item) : formatWholesale(item);
}

function formatAsOf(date) {
  if (!date) {
    return "";
  }
  const parsed = new Date(`${date}T00:00:00+05:30`);
  if (Number.isNaN(parsed.getTime())) {
    return date;
  }
  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function cellValue(item, priceType) {
  if (!item) {
    return null;
  }
  if (priceType === "retail") {
    return item.retailMin ?? item.retailMax ?? null;
  }
  return item.wholesale;
}

function cheapestCityId(pricesByCity, priceType) {
  let min = Infinity;
  let winner = null;
  Object.entries(pricesByCity).forEach(([cityId, item]) => {
    const value = cellValue(item, priceType);
    if (value != null && value < min) {
      min = value;
      winner = cityId;
    }
  });
  return winner;
}

const stickyCellSx = {
  position: "sticky",
  left: 0,
  bgcolor: "#16162e",
  zIndex: 1,
};

function ItemMark({ id, emojiFor, imageFor }) {
  const src = imageFor?.(id);
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt=""
        sx={{
          width: 28,
          height: 28,
          objectFit: "contain",
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <Box component="span" aria-hidden>
      {emojiFor(id)}
    </Box>
  );
}

function ProducePricesPage({
  title,
  itemName,
  itemNamePlural,
  apiPath,
  itemsKey,
  loadFeatured,
  saveFeatured,
  emojiFor,
  imageFor,
  footnote,
}) {
  const [cities, setCities] = useState([]);
  const [asOf, setAsOf] = useState("");
  const [priceType, setPriceType] = useState("retail");
  const [showAll, setShowAll] = useState(false);
  const [featuredIds, setFeaturedIds] = useState(loadFeatured);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const loadPrices = useCallback(
    async (force = false) => {
      setLoading(true);
      setStatus("");

      try {
        const response = await fetch(
          force ? `${apiPath}?refresh=1` : apiPath
        );
        const text = await response.text();
        let payload;
        try {
          payload = JSON.parse(text);
        } catch {
          const hint =
            text.trimStart().startsWith("<!DOCTYPE") ||
            text.trimStart().startsWith("<html")
              ? `Got HTML instead of JSON — use npm start (runs the prices API on :3001), or open the deployed site on Vercel.`
              : "Response was not valid JSON.";
          throw new Error(hint);
        }

        if (!response.ok) {
          const msg =
            payload?.details || payload?.error || "Unknown API error";
          throw new Error(
            typeof msg === "string" ? msg : JSON.stringify(msg)
          );
        }

        setCities(payload.cities || []);
        setAsOf(payload.asOf || "");
        setStatus("");
      } catch (error) {
        setStatus(`Failed to load prices: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    [apiPath]
  );

  useEffect(() => {
    loadPrices();
    const id = setInterval(() => loadPrices(), 300000);
    return () => clearInterval(id);
  }, [loadPrices]);

  const failedCities = cities.filter((city) => city.error);

  const featuredIdSet = useMemo(() => new Set(featuredIds), [featuredIds]);
  const featuredOrder = useMemo(
    () => new Map(featuredIds.map((id, index) => [id, index])),
    [featuredIds]
  );

  const toggleFeatured = useCallback(
    (id) => {
      setFeaturedIds((current) => {
        const next = current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id];
        saveFeatured(next);
        return next;
      });
    },
    [saveFeatured]
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byId = new Map();

    cities.forEach((city) => {
      (city[itemsKey] || []).forEach((item) => {
        if (!byId.has(item.id)) {
          byId.set(item.id, {
            id: item.id,
            label: item.label,
            prices: {},
          });
        }
        byId.get(item.id).prices[city.id] = item;
      });
    });

    return [...byId.values()]
      .filter((row) => {
        if (q) {
          return row.label.toLowerCase().includes(q);
        }
        return showAll || featuredIdSet.has(row.id);
      })
      .sort((a, b) => {
        const aFeatured = featuredOrder.has(a.id);
        const bFeatured = featuredOrder.has(b.id);
        if (aFeatured && bFeatured) {
          return featuredOrder.get(a.id) - featuredOrder.get(b.id);
        }
        if (aFeatured !== bFeatured) {
          return aFeatured ? -1 : 1;
        }
        return a.label.localeCompare(b.label);
      });
  }, [cities, featuredIdSet, featuredOrder, itemsKey, query, showAll]);

  const tableLabel = `${itemName} prices by city`;
  const itemTitle = itemName.charAt(0).toUpperCase() + itemName.slice(1);

  return (
    <Box component="main" sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rates across major cities
              {asOf ? ` · ${formatAsOf(asOf)}` : ""}.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => loadPrices(true)}
            disabled={loading}
            sx={{ alignSelf: { md: "center" } }}
          >
            Refresh
          </Button>
        </Stack>

        {loading ? (
          <LinearProgress
            aria-label={`Loading ${itemNamePlural} prices`}
            sx={{ borderRadius: 1 }}
          />
        ) : null}

        {status ? (
          <Alert severity={status.startsWith("Failed") ? "error" : "info"}>
            {status}
          </Alert>
        ) : null}

        {failedCities.length > 0 && !status.startsWith("Failed") ? (
          <Alert severity="warning">
            Could not refresh {failedCities.map((city) => city.label).join(", ")}
            .
          </Alert>
        ) : null}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Tabs
            value={priceType}
            onChange={(_event, next) => setPriceType(next)}
            aria-label="Price type"
          >
            <Tab value="retail" label="Retail" />
            <Tab value="wholesale" label="Wholesale" />
          </Tabs>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="Featured"
                color={showAll ? "default" : "primary"}
                variant={showAll ? "outlined" : "filled"}
                onClick={() => setShowAll(false)}
              />
              <Chip
                label={`All ${itemNamePlural}`}
                color={showAll ? "primary" : "default"}
                variant={showAll ? "filled" : "outlined"}
                onClick={() => setShowAll(true)}
              />
            </Stack>
            <TextField
              size="small"
              label={`Search ${itemNamePlural}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              sx={{ minWidth: { sm: 220 } }}
            />
          </Stack>
        </Stack>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            bgcolor: "var(--card-bg)",
            border: "1px solid var(--card-border)",
            borderRadius: "20px",
            boxShadow: "var(--shadow-md)",
            overflowX: "auto",
          }}
        >
          <Table size="small" stickyHeader aria-label={tableLabel}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...stickyCellSx,
                    fontWeight: 700,
                    minWidth: 160,
                    bgcolor: "#1c1c38",
                  }}
                >
                  {itemTitle}
                </TableCell>
                {CITIES.map((city) => (
                  <TableCell
                    key={city.id}
                    align="right"
                    sx={{ minWidth: 104, fontWeight: 700, bgcolor: "#1c1c38" }}
                  >
                    {city.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={CITIES.length + 1}>
                    <Typography variant="body2" color="text.secondary">
                      {showAll || query.trim()
                        ? `No ${itemNamePlural} match this filter.`
                        : `No featured ${itemNamePlural}. Open All ${itemNamePlural} and star the ones you want.`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const cheapest = cheapestCityId(row.prices, priceType);
                  const isFeatured = featuredIdSet.has(row.id);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{ ...stickyCellSx, fontWeight: 600 }}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip
                            title={
                              isFeatured
                                ? "Remove from featured"
                                : "Add to featured"
                            }
                          >
                            <IconButton
                              size="small"
                              aria-pressed={isFeatured}
                              aria-label={
                                isFeatured
                                  ? `Remove ${row.label} from featured`
                                  : `Add ${row.label} to featured`
                              }
                              onClick={() => toggleFeatured(row.id)}
                              sx={{
                                color: isFeatured
                                  ? "warning.main"
                                  : "text.secondary",
                                fontSize: "1.05rem",
                              }}
                            >
                              {isFeatured ? "★" : "☆"}
                            </IconButton>
                          </Tooltip>
                          <ItemMark
                            id={row.id}
                            emojiFor={emojiFor}
                            imageFor={imageFor}
                          />
                          <Box component="span">{row.label}</Box>
                        </Stack>
                      </TableCell>
                      {CITIES.map((city) => {
                        const item = row.prices[city.id];
                        const isCheapest =
                          cheapest === city.id &&
                          cellValue(item, priceType) != null;
                        return (
                          <TableCell
                            key={city.id}
                            align="right"
                            sx={{
                              color: isCheapest ? "success.main" : "inherit",
                              fontWeight: isCheapest ? 700 : 400,
                            }}
                          >
                            {formatCell(item, priceType)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="body2" color="text.secondary">
          {footnote ||
            `Daily mandi listings. Retail is an estimate and can vary by neighbourhood. Star a ${itemName} to keep it in Featured. Lowest price in each row is highlighted.`}
        </Typography>
      </Stack>
    </Box>
  );
}

export default ProducePricesPage;
